<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { createTicket, fetchMyTickets } from '@/api/tickets';
import AttachButton from '@/components/AttachButton.vue';
import AttachmentChips from '@/components/AttachmentChips.vue';
import { useAttachments } from '@/composables/useAttachments';
import { usePolling } from '@/composables/usePolling';
import { formatRelativeTime, formatStatus } from '@/tools/format';
import type { WidgetTicket } from '@/api/types';

const emit = defineEmits<{
  select: [ticket: WidgetTicket];
}>();

const tickets = ref<WidgetTicket[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);

const title = ref('');
const body = ref('');
const creating = ref(false);
const createError = ref<string | null>(null);

const attachments = useAttachments();

const load = async (): Promise<void> => {
  loading.value = true;
  loadError.value = null;

  try {
    tickets.value = await fetchMyTickets();
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Could not load your conversations.';
  } finally {
    loading.value = false;
  }
};

const submit = async (): Promise<void> => {
  const nextTitle = title.value.trim();
  const nextBody = body.value.trim();

  if (!nextTitle || !nextBody || creating.value) {
    return;
  }

  creating.value = true;
  createError.value = null;

  try {
    // The images first, so a file that cannot be stored opens no conversation.
    const attachmentIds = await attachments.upload();
    const ticket = await createTicket(nextTitle, nextBody, attachmentIds);

    title.value = '';
    body.value = '';
    attachments.clear();
    tickets.value = [ticket, ...tickets.value];

    emit('select', ticket);
  } catch (error) {
    createError.value = error instanceof Error ? error.message : 'Could not start the conversation.';
  } finally {
    creating.value = false;
  }
};

/**
 * Slower than an open thread: the list answers "did anything move", and a
 * visitor looking at it is deciding what to open, not waiting on one reply.
 */
const REFRESH_INTERVAL_MS = 10_000;

/**
 * Re-read the list, so a thread an agent just answered moves to the top.
 *
 * Replaces rather than merges — the API's order IS the information here — but
 * only once the first load has succeeded, and never while a new conversation
 * is being filed: that row is added locally the moment it is created, and a
 * response that left before it existed would take it away again.
 */
const refresh = async (): Promise<void> => {
  if (loading.value || loadError.value || creating.value) {
    return;
  }

  const latest = await fetchMyTickets();

  if (!creating.value) {
    tickets.value = latest;
  }
};

usePolling(refresh, REFRESH_INTERVAL_MS);

onMounted(load);
</script>

<template>
  <div class="screen">
    <section class="composer">
      <h2 class="composer__heading">
        New conversation
      </h2>

      <!-- `maxlength` mirrors the API's own limits (title 1..300, body
           1..20000). Over the limit the server answers with a validation error
           rather than truncating, so stopping it here saves a round trip that
           could only ever fail. -->
      <input
        data-testid="widget-new-title"
        v-model="title"
        class="field"
        type="text"
        placeholder="What do you need help with?"
        maxlength="300"
        :disabled="creating"
      >
      <!-- Plain text only: rich text is deliberately out of scope for the
           skeleton. Images ride beside the text, not inside it. -->
      <textarea
        data-testid="widget-new-body"
        v-model="body"
        class="field field--multiline"
        rows="3"
        placeholder="Describe the problem"
        maxlength="20000"
        :disabled="creating"
      />

      <AttachmentChips
        :items="attachments.pending.value"
        :disabled="creating"
        @remove="attachments.remove"
      />

      <p
        v-if="createError ?? attachments.refusal.value"
        class="error"
        data-testid="widget-new-error"
      >
        {{ createError ?? attachments.refusal.value }}
      </p>

      <div class="composer__actions">
        <AttachButton
          v-if="attachments.available.value"
          test-id="widget-new-attach"
          :disabled="creating"
          @pick="attachments.add"
        />
        <button
          data-testid="widget-new-submit"
          type="button"
          class="button"
          :disabled="creating || !title.trim() || !body.trim()"
          @click="submit"
        >
          {{ creating ? 'Sending…' : 'Send' }}
        </button>
      </div>
    </section>

    <section class="list">
      <h2 class="list__heading">
        Your conversations
      </h2>

      <p
        v-if="loading"
        class="hint"
      >
        Loading…
      </p>

      <template v-else-if="loadError">
        <p class="error">
          {{ loadError }}
        </p>
        <button
          type="button"
          class="button button--quiet"
          @click="load"
        >
          Try again
        </button>
      </template>

      <p
        v-else-if="tickets.length === 0"
        class="hint"
      >
        Nothing here yet — start a conversation above.
      </p>

      <ul
        v-else
        class="tickets"
        data-testid="widget-ticket-list"
      >
        <li
          v-for="ticket in tickets"
          :key="ticket.id"
        >
          <button
            data-testid="widget-ticket"
            type="button"
            class="ticket"
            @click="emit('select', ticket)"
          >
            <span
              class="ticket__title"
              :class="{ 'ticket__title--unread': ticket.unread }"
            >
              <!-- A dot AND a heavier title: the dot alone is easy to miss in a
                   list of one, and weight alone says nothing to someone who
                   has never seen the list without it. -->
              <span
                v-if="ticket.unread"
                data-testid="widget-ticket-unread"
                class="ticket__unread"
                title="New reply"
              />
              {{ ticket.title }}
            </span>
            <span class="ticket__meta">
              <span class="ticket__status">{{ formatStatus(ticket.status) }}</span>
              <span>{{ formatRelativeTime(ticket.lastMessageAt) }}</span>
            </span>
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.screen {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  overflow-y: auto;
}

.composer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.composer__actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.composer__heading,
.list__heading {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--w-text-muted);
}

.field {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--w-border);
  border-radius: 8px;
  background: var(--w-bg);
  outline: none;
}

.field:focus {
  border-color: var(--w-accent);
}

.field--multiline {
  resize: vertical;
}

.button {
  align-self: flex-start;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: var(--w-accent);
  color: var(--w-accent-text);
  font-weight: 600;
}

.button:disabled {
  opacity: 0.5;
  cursor: default;
}

.button--quiet {
  background: var(--w-bg-subtle);
  color: var(--w-text);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tickets {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ticket {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--w-border);
  border-radius: var(--w-radius);
  background: var(--w-bg);
  text-align: left;
}

.ticket:hover {
  background: var(--w-bg-subtle);
}

.ticket__title {
  font-weight: 500;
}

.ticket__title--unread {
  font-weight: 700;
}

.ticket__unread {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  background: var(--w-accent);
  vertical-align: middle;
}

.ticket__meta {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: var(--w-text-muted);
}

.ticket__status {
  text-transform: capitalize;
}

.hint {
  margin: 0;
  font-size: 13px;
  color: var(--w-text-muted);
}

.error {
  margin: 0;
  font-size: 13px;
  color: var(--w-danger);
}
</style>
